import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Status } from "src/app/models/statusEnums";

@Injectable({
    providedIn: "root",
})
export class ProductsCalculationUtilsService {
    constructor() {}

    setBin(ordQty, bins) {
        const selectedBin = bins?.find((bin) => bin.availQty > 0 && ordQty <= bin.availQty && bin.isDefault);
        if (selectedBin) {
            return selectedBin;
        } else {
            const defaultBin = bins?.find((bin) => bin.isDefault);
            return defaultBin;
        }
    }

    calculateProductsTax(taxRate: number, subTotal: number) {
        return subTotal * (Number(taxRate) / 100);
    }

    calculateDiscount(disName: string, disValue: number, orderSubtotal: number): number {
        let discount = 0;
        if (disName === "%" || disName === "Percentage") {
            discount = orderSubtotal * (disValue / 100);
        } else if (disName === "$" || disName === "Amount") {
            discount = disValue;
        }
        return isNaN(discount) ? 0 : discount;
    }

    getTotalTaxAmount(taxDetails: any[]): number {
        return taxDetails?.reduce((sum, item) => sum + (item.taxAmount ?? 0), 0);
    }

    calculateHandlingFee(feeName, feeAmount, sumTotal, isFooterAdditionalFeeEnabled): number {
        let addFeeAmount = 0;
        if (isFooterAdditionalFeeEnabled) {
            if (feeName == "%" || feeName == "Percentage") {
                addFeeAmount = Number(sumTotal * (Number(feeAmount) / 100));
            } else if (feeName == "$" || feeName == "Amount") {
                addFeeAmount = Number(feeAmount);
            }
        }
        return isNaN(addFeeAmount) ? 0 : addFeeAmount;
    }

    calculateAdditionalDiscount(form: FormGroup, totalAmount: number): number {
        const additionalDiscountType = form.get("additionalDiscountType")?.value;
        const additionalDiscountRate = form.get("additionalDiscountRate")?.value;

        if (additionalDiscountType?.type?.description === "%" || additionalDiscountType?.type?.description === "Percentage") {
            return totalAmount * (additionalDiscountRate / 100);
        } else {
            return additionalDiscountRate ?? 0;
        }
    }

    getProductsApplicableSubTaxes(product, allTaxes, zipcode?, stateId?, taxSettings?, taxConfig?) {
        let allTaxRates = [];
        let yesTaxOutsideStates = taxSettings ? taxSettings.find((s) => s?.code == "YesTaxOutsideStates")?.enabled : false;
        let taxes = allTaxes?.filter((t) => t.statusId == Status.Active);
        let subTaxes = [];

        /**
         * Step 1: Check based on zipcode & product category
         */
        if (taxes && taxes.length > 0) {
            taxes.forEach((tax) => {
                let zipCodes = tax.zipCode && tax.zipCode.length > 0 ? tax.zipCode.split(",") : [];
                let zipcodeExists = zipCodes.includes(zipcode);
                if (zipcodeExists) {
                    let taxAdded = false;

                    // find tax by product category
                    product?.productCategory?.forEach((pCategory) => {
                        const rate = tax?.taxProductCategory?.find((c) => c.categoryId == pCategory.categoryId && c.statusId == Status.Active);
                        if (rate) {
                            subTaxes.push(tax);
                            taxAdded = true;
                        }
                    });

                    if (!taxAdded) {
                        let activeProductCategories = tax?.taxProductCategory?.find((c) => c.statusId == Status.Active);
                        if (!activeProductCategories) {
                            // no category defined in this tax
                            subTaxes.push(tax);
                        }
                    }
                }
            });
        }

        /**
         * Step 2: Check based on state & product category
         * if there is no tax defined based on zipcode , then second step is to check based on state & product category
         */
        if (taxes && taxes.length > 0 && subTaxes.length == 0) {
            allTaxRates = taxes.filter((t) => t.stateId == stateId);
            let taxAdded = false;

            // find tax by product category
            product?.productCategory?.forEach((pCategory) => {
                allTaxRates?.forEach((tax) => {
                    const rate = tax?.taxProductCategory?.find((c) => c.categoryId == pCategory.categoryId && c.statusId == Status.Active);
                    if (rate) {
                        subTaxes.push(tax);
                        taxAdded = true;
                    }
                });
            });

            // if product category wise no tax found, then default tax will be applied
            // in this case default tax is, state tax is defined without any category
            if (!taxAdded) {
                allTaxRates?.forEach((tax) => {
                    let activeProductCategories = tax?.taxProductCategory?.find((c) => c.statusId == Status.Active);
                    if (!activeProductCategories) {
                        // no category defined in this tax
                        subTaxes.push(tax);
                    }
                });
            }

            // yesTaxOutsideStates is set to true and no state tax is defined
            if (subTaxes?.length == 0 && yesTaxOutsideStates) {
                let defaultTax = allTaxRates?.find((t) => t.isDefault == true && t.statusId == Status.Active);
                if (defaultTax) {
                    subTaxes.push(defaultTax);
                }
            }
        }

        /**
         * Step 3: Check based on country & product category
         * if there is no tax defined based on zipcode & state , then third step is to check based on country & product category
         */
        if (taxes && taxes.length > 0 && subTaxes.length == 0) {
            allTaxRates = taxes.filter((t) => t.stateId == 0 && t.zipCode == ""); // country tax where state is not defined
            let taxAdded = false;

            // find tax by product category
            product?.productCategory?.forEach((pCategory) => {
                allTaxRates?.forEach((tax) => {
                    const rate = tax?.taxProductCategory?.find((c) => c.categoryId == pCategory.categoryId && c.statusId == Status.Active);
                    if (rate) {
                        subTaxes.push(tax);
                        taxAdded = true;
                    }
                });
            });

            // if product category wise no tax found, then default tax will be applied
            // in this case default tax is, country tax is defined without any category
            if (!taxAdded) {
                allTaxRates?.forEach((tax) => {
                    let activeProductCategories = tax?.taxProductCategory?.find((c) => c.statusId == Status.Active);
                    if (!activeProductCategories) {
                        // no category defined in this tax
                        subTaxes.push(tax);
                    }
                });
            }
        }

        /**
         * Step 4: Apply Default Tax
         */
        if (subTaxes.length == 0 && yesTaxOutsideStates) {
            if (taxConfig && taxConfig.length > 0) {
                taxConfig?.forEach((tc) => {
                    let tax = tc?.tax?.find((t) => t.isDefault == true && t.statusId == Status.Active);
                    if (tax) {
                        subTaxes.push(tax);
                    }
                });
            }
        }
        return subTaxes;
    }
}
